import { useState } from "react";
import { useBalance } from "wagmi";
import { SWAP_SUPPORTED_CHAINS, API_SWAP_CHAIN_IDS, USDC_BY_CHAIN } from "../utils/constants";
import StatusBox from "./StatusBox";

const SwapCard = ({
  isConnected,
  address,
  loading,
  status,
  error,
  txHash,
  onSwap,
  onGetQuote,
  quoteInfo,
  quoteLoading,
}) => {
  const [swapAmount, setSwapAmount] = useState("0.001");
  const [swapChainId, setSwapChainId] = useState(11155111); // default ETH Sepolia
  const [direction, setDirection] = useState("ethToUsdc"); // ethToUsdc or usdcToEth
  const [apiKey, setApiKey] = useState("");
  // Track which inputs the current quote corresponds to  (detect stale quotes)
  const [quotedForInputs, setQuotedForInputs] = useState(null);

  const { data: balanceData } = useBalance({
    address,
    chainId: swapChainId,
    token: direction === "usdcToEth" ? USDC_BY_CHAIN[swapChainId] : undefined,
    query: {
      enabled: !!address,
    },
  });

  const handleMax = () => {
    if (balanceData) {
      setSwapAmount(balanceData.formatted);
      setQuotedForInputs(null); // stale after max
    }
  };

  // Trading API chains require an API key; V4 chains (Base Sepolia) do not
  const needsApiKey = API_SWAP_CHAIN_IDS.has(swapChainId);
  const isV4Chain = !needsApiKey;

  // Key representing the current form inputs — used to detect if the quote is stale
  const inputsKey = `${swapAmount}|${swapChainId}|${direction}|${apiKey}`;
  const quoteIsStale = quoteInfo !== null && quotedForInputs !== inputsKey;
  const quoteIsValid = quoteInfo !== null && !quoteIsStale;

  const handleGetQuote = async () => {
    await onGetQuote({ amount: swapAmount, swapChainId, direction, apiKey });
    setQuotedForInputs(inputsKey);
  };

  const handleSwap = () => {
    if (!address || !swapAmount) return;
    if (needsApiKey && !apiKey) return;
    onSwap({ amount: swapAmount, swapChainId, direction, apiKey });
  };

  // Helper to reset quote when an input field changes
  const resetQuote = () => setQuotedForInputs(null);

  const tokenIn = direction === "ethToUsdc" ? "ETH" : "USDC";
  const tokenOut = direction === "ethToUsdc" ? "USDC" : "ETH";

  return (
    <div className="mb-6 rounded-3xl border border-gray-200 bg-white shadow-lg sm:mb-8">
      <div className="p-6 sm:p-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
            <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none">
              <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Swap</h2>
            <p className="text-sm text-gray-500">
              {isV4Chain
                ? "ETH ↔ USDC via Uniswap V4 Universal Router"
                : "ETH ↔ USDC via Uniswap Trading API"}
            </p>
          </div>
        </div>

        {/* API Key Input — only for Trading API chains */}
        {needsApiKey && (
        <div className="mb-4 flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Uniswap API Key
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); resetQuote(); }}
            placeholder="Enter your Uniswap API key"
            disabled={loading}
            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/10 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-xs text-gray-400">
            Get one at{" "}
            <a
              href="https://hub.uniswap.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-500 hover:underline"
            >
              hub.uniswap.org
            </a>
          </p>
        </div>
        )}

        {/* V4 info banner for Base Sepolia */}
        {isV4Chain && (
          <div className="mb-4 rounded-xl border border-purple-100 bg-purple-50 px-4 py-3">
            <p className="text-xs font-medium text-purple-700">
              ⚡ Using Uniswap V4 Universal Router — no API key required.
              Permit2 approvals will be requested on first USDC→ETH swap.
            </p>
          </div>
        )}

        {/* Chain Selector */}
        <div className="mb-4 flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Swap on Chain
          </label>
          <select
            value={swapChainId}
            onChange={(e) => { setSwapChainId(Number(e.target.value)); resetQuote(); }}
            disabled={loading}
            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {SWAP_SUPPORTED_CHAINS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Direction Toggle */}
        <div className="mb-4 flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Direction</label>
          <div className="flex gap-2">
            <button
              onClick={() => { setDirection("ethToUsdc"); resetQuote(); }}
              disabled={loading}
              className={`flex-1 rounded-xl border-2 py-3 px-4 text-sm font-semibold transition-all ${
                direction === "ethToUsdc"
                  ? "border-purple-500 bg-purple-50 text-purple-700"
                  : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              ETH → USDC
            </button>
            <button
              onClick={() => { setDirection("usdcToEth"); resetQuote(); }}
              disabled={loading}
              className={`flex-1 rounded-xl border-2 py-3 px-4 text-sm font-semibold transition-all ${
                direction === "usdcToEth"
                  ? "border-purple-500 bg-purple-50 text-purple-700"
                  : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              USDC → ETH
            </button>
          </div>
        </div>

        {/* Amount Input */}
        <div className="mb-6 flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-gray-700">
              Amount ({tokenIn})
            </label>
            {balanceData && (
              <span className="text-xs font-medium text-gray-500">
                Balance: {Number(balanceData.formatted).toFixed(4)} {balanceData.symbol}
              </span>
            )}
          </div>
          <div className="relative">
            <input
              type="number"
              value={swapAmount}
              onChange={(e) => { setSwapAmount(e.target.value); resetQuote(); }}
              placeholder="0.00"
              min="0"
              step={direction === "ethToUsdc" ? "0.001" : "1"}
              disabled={loading}
              inputMode="decimal"
              className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 py-4 pl-5 pr-[210px] text-2xl font-bold text-gray-900 placeholder:text-gray-300 transition-all focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-purple-500/5 disabled:cursor-not-allowed disabled:opacity-50 sm:text-3xl"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <button
                onClick={handleMax}
                disabled={loading || !balanceData}
                title="Max balance"
                className="rounded-lg bg-purple-100 px-3 py-1.5 text-xs font-bold tracking-wide text-purple-700 transition-colors hover:bg-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                MAX
              </button>
              <span className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm">
                {tokenIn}
              </span>
              <svg className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none">
                <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm">
                {tokenOut}
              </span>
            </div>
          </div>
        </div>

        {/* Quote preview */}
        {quoteIsValid && (
          <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Estimated Output</span>
              <span className="text-xs text-green-600">{quoteInfo.routing}</span>
            </div>
            <p className="mt-1 text-2xl font-bold text-green-800">
              {quoteInfo.amountOutFormatted} <span className="text-base font-semibold">{quoteInfo.tokenOut}</span>
            </p>
            <p className="mt-1 text-xs text-green-600">Price includes 5% slippage tolerance</p>
          </div>
        )}
        {quoteIsStale && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2">
            <p className="text-xs font-medium text-amber-700">⚠ Inputs changed — re-quote to get a fresh estimate.</p>
          </div>
        )}

        {/* Get Quote / Confirm Swap buttons */}
        {!quoteIsValid ? (
          <button
            onClick={handleGetQuote}
            disabled={!isConnected || quoteLoading || loading || !address || !swapAmount || (needsApiKey && !apiKey)}
            className="mt-2 w-full rounded-2xl border-2 border-purple-500 bg-white py-4 text-base font-bold text-purple-700 shadow transition-all hover:bg-purple-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:text-lg"
          >
            {quoteLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Getting Quote...
              </span>
            ) : !isConnected ? (
              "Connect Wallet to Quote"
            ) : needsApiKey && !apiKey ? (
              "Enter API Key"
            ) : (
              `Get Quote: ${tokenIn} → ${tokenOut}`
            )}
          </button>
        ) : (
          <button
            onClick={handleSwap}
            disabled={!isConnected || loading || !address || !swapAmount || (needsApiKey && !apiKey)}
            className="mt-2 w-full rounded-2xl bg-purple-600 py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-purple-700 hover:shadow-xl active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-purple-600 disabled:hover:shadow-lg sm:text-lg"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Swapping...
              </span>
            ) : (
              `Confirm Swap: ${tokenIn} → ${tokenOut}`
            )}
          </button>
        )}

        {/* Status/Error Display */}
        {(status || error) && (
          <div className="mt-6">
            <StatusBox status={status} error={error} />
          </div>
        )}

        {/* Tx Hash */}
        {txHash && !error && (
          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <span className="text-xs font-medium text-gray-500">Transaction: </span>
            <span className="break-all font-mono text-xs text-gray-900">{txHash}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SwapCard;
